import React from "react";
import {
  Box,
  Typography,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { colorByFundId } from "../../../utils/colorEnumFormatter";
import { formatHandlerCode, formatCurrency } from "../../../utils/namesFormater";
import { getColorForPercentileNumber } from "../../../utils/fundsFormatter";

interface FundAccordionListProps {
  title: string;
  fundIds: number[];
  funds: any[];
  borderColor: string;
  isVisible: boolean;
  onToggleVisibility: () => void;
  selectedFundIds: number[];
  onRemoveFund: (id: number) => void;
  fundAmountMap?: Map<number, number>;
}

const FundAccordionList: React.FC<FundAccordionListProps> = ({
  title,
  fundIds,
  funds,
  borderColor,
  isVisible,
  onToggleVisibility,
  selectedFundIds,
  onRemoveFund,
  fundAmountMap,
}) => {
  if (fundIds.length === 0) return null;

  const sortedFundIds = [...fundIds].sort(
    (a, b) => (fundAmountMap?.get(b) ?? 0) - (fundAmountMap?.get(a) ?? 0)
  );

  const maxAmount = Math.max(
    1,
    ...sortedFundIds.map((id) => fundAmountMap?.get(id) ?? 0)
  );

  return (
    <Box sx={{ mb: 3 }}>
      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
        <Button
          variant="text"
          size="small"
          onClick={onToggleVisibility}
          sx={{
            minWidth: 'auto',
            p: 0.5,
            color: isVisible ? 'text.secondary' : 'text.disabled',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {isVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
        </Button>
        <Typography variant="subtitle1">{title}</Typography>
      </Box>
      <Box display="flex" flexDirection="column" gap={1}>
        {sortedFundIds.map((id) => {
          const fund = funds.find((f) => f.fund_id === id);
          if (!fund) return null;

          const fundAmount = fundAmountMap?.get(id);
          const barWidthPercent = fundAmount ? Math.max(2, (fundAmount / maxAmount) * 100) : 0;
          const percentileColor =
            fund.percentile_12m_yield !== null && fund.percentile_12m_yield !== undefined
              ? getColorForPercentileNumber(fund.percentile_12m_yield)
              : "#BDBDBD";

          return (
            <Accordion
              key={id}
              sx={{
                bgcolor: "#f5f5f5",
                borderRight: `4px solid ${borderColor}`,
                borderRadius: '4px !important',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  "& .MuiAccordionSummary-content": {
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  },
                }}
              >
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFund(id);
                  }}
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: colorByFundId(id),
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "0.9rem",
                    minWidth: "50px",
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover": {
                      opacity: 0.8,
                    },
                  }}
                  title="הסר בחירה"
                >
                  {id}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
                  {fund.fund_name || "ללא שם"}
                </Typography>
                {fundAmount !== undefined && (
                  <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.25}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(fundAmount)}
                    </Typography>
                    <Box
                      sx={{
                        width: 75,
                        height: 4,
                        borderRadius: 2,
                        bgcolor: "rgba(0,0,0,0.08)",
                        overflow: "hidden",
                        position: "relative",
                      }}
                      title={
                        fund.percentile_12m_yield !== null && fund.percentile_12m_yield !== undefined
                          ? `דירוג תשואה 12M: ${fund.percentile_12m_yield.toFixed(0)}`
                          : undefined
                      }
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          width: `${barWidthPercent}%`,
                          height: "100%",
                          borderRadius: 2,
                          bgcolor: percentileColor,
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2} sx={{ direction: "rtl" }}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>התמחות:</strong> {fund.specialization || "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>תת התמחות:</strong> {fund.sub_specialization || "-"}
                      {fund.original_sub_specialization &&
                       fund.original_sub_specialization !== fund.sub_specialization &&
                       ` (${fund.original_sub_specialization})`}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>חשיפה למניות:</strong> {fund.stock_market_percent !== null && fund.stock_market_percent !== undefined ? `${fund.stock_market_percent.toFixed(1)}%` : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>חשיפה לחו"ל:</strong> {fund.foreign_exposure_percent !== null && fund.foreign_exposure_percent !== undefined ? `${fund.foreign_exposure_percent.toFixed(1)}%` : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>חשיפה למט"ח:</strong> {fund.foreign_currency_exposure_percent !== null && fund.foreign_currency_exposure_percent !== undefined ? `${fund.foreign_currency_exposure_percent.toFixed(1)}%` : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>יצרן:</strong> {fund.managing_corporation_legal_id ? formatHandlerCode(String(fund.managing_corporation_legal_id)) : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>נכסים (מיליוני ש"ח):</strong> {fund.total_assets !== null && fund.total_assets !== undefined ? (fund.total_assets / 1000000).toFixed(0) : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>תשואה 12M:</strong> {fund.cumulative_12m_yield !== null && fund.cumulative_12m_yield !== undefined ? `${fund.cumulative_12m_yield.toFixed(2)}%` : "-"}
                      {" | "}
                      <strong>תשואה 3Y:</strong> {fund.avg_annual_yield_trailing_3yrs !== null && fund.avg_annual_yield_trailing_3yrs !== undefined ? `${fund.avg_annual_yield_trailing_3yrs.toFixed(2)}%` : "-"}
                      {" | "}
                      <strong>תשואה 5Y:</strong> {fund.avg_annual_yield_trailing_5yrs !== null && fund.avg_annual_yield_trailing_5yrs !== undefined ? `${fund.avg_annual_yield_trailing_5yrs.toFixed(2)}%` : "-"}
                    </Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </Box>
  );
};

export default FundAccordionList;
